import { uniqBy } from "lodash";
import {
  languageDisplayNames,
  technologyDisplayNames,
  focusDisplayNames,
} from "sj-ui-toolkit/src/constants/content";
import type { CatalogTagOption } from "sj-ui-toolkit/src/client/svcContent";
import { CONTENT_TYPE_TO_LESSON_TYPE } from "@/api/resources";

// ─── Language normalisation ───────────────────────────────────────────────────

const LANGUAGE_VARIANTS: Record<string, string[]> = {
  cpp: ["c++", "cpp", "cplusplus"],
  "c++": ["c++", "cpp", "cplusplus"],
  cplusplus: ["c++", "cpp", "cplusplus"],
  dotnet: ["dotnet", "csharp", "c#"],
  "c#": ["dotnet", "csharp", "c#"],
  csharp: ["dotnet", "csharp", "c#"],
};

const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  cpp: "cpp",
  "c++": "cpp",
  cplusplus: "cpp",
  dotnet: "dotnet",
  "c#": "dotnet",
  csharp: "dotnet",
};

const normalizeLanguageName = (lang: string): string =>
  LANGUAGE_DISPLAY_NAMES[lang.toLowerCase()] ?? lang;

const getLanguageDisplayName = (lang: string): string => {
  const normalized = normalizeLanguageName(lang);
  return (languageDisplayNames as Record<string, string>)[normalized] ?? normalized;
};

const getTechnologyDisplayName = (tech: string): string =>
  (technologyDisplayNames as Record<string, string>)[tech.toLowerCase()] ?? tech;

const getFocusDisplayName = (focus: string): string =>
  (focusDisplayNames as Record<string, string>)[focus.toLowerCase()] ?? focus;

export const expandLanguageVariants = (languages: string[] = []): string[] =>
  uniqBy(
    languages.flatMap(lang => LANGUAGE_VARIANTS[lang] ?? [lang]),
    (v: string) => v.toLowerCase()
  );

// ─── Filter option builders ───────────────────────────────────────────────────

export interface FilterOption {
  name: string;
  value: string;
}

export interface FilterGroup {
  title: string;
  label: string;
  filters: FilterOption[];
}

export const setLanguages = ({
  experimentLanguagesList = [],
  tagsData = {},
}: {
  experimentLanguagesList?: Array<{ name: string }>;
  tagsData?: Record<string, CatalogTagOption[]>;
}): FilterOption[] => {
  const experimentLanguages: FilterOption[] = experimentLanguagesList
    .filter(e => e.name)
    .map(e => ({
      name: getLanguageDisplayName(e.name),
      value: normalizeLanguageName(e.name),
    }));

  const lessonLanguages: FilterOption[] = (tagsData.languages ?? [])
    .filter(t => t.id)
    .map(t => ({
      name: getLanguageDisplayName(t.id),
      value: normalizeLanguageName(t.id),
    }));

  return uniqBy([...experimentLanguages, ...lessonLanguages], "value").sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

export const setTechnologies = ({
  tagsData = {},
}: {
  tagsData?: Record<string, CatalogTagOption[]>;
}): FilterOption[] =>
  uniqBy(
    (tagsData.technologies ?? [])
      .filter(t => t.id)
      .map(t => ({
        name: getTechnologyDisplayName(t.id),
        value: t.id.toLowerCase(),
      })),
    "value"
  ).sort((a, b) => a.name.localeCompare(b.name));

export const setFocus = ({
  tagsData = {},
}: {
  tagsData?: Record<string, CatalogTagOption[]>;
}): FilterOption[] =>
  uniqBy(
    (tagsData.focus ?? [])
      .filter(t => t.id)
      .map(t => ({
        name: getFocusDisplayName(t.id),
        value: t.id.toLowerCase(),
      })),
    "value"
  ).sort((a, b) => a.name.localeCompare(b.name));

const DIFFICULTY_OPTIONS: FilterOption[] = [
  { value: "awareness", name: "Security Awareness" },
  { value: "basic", name: "Basic" },
  { value: "intermediate", name: "Intermediate" },
  { value: "advanced", name: "Advanced" },
];

export const setLevel = ({
  experimentDifficultiesList = [],
}: {
  experimentDifficultiesList?: Array<{ name: string }>;
}): FilterOption[] => {
  const experimentsOptions = experimentDifficultiesList
    .filter(e => e.name)
    .map(e => ({ name: e.name, value: e.name }));
  return uniqBy([...experimentsOptions, ...DIFFICULTY_OPTIONS], "value");
};

// "Coding Exercises" is the legacy /admin/experiments endpoint, not a svc-content lesson type, so
// it has no entry in contentTypeOptions; callers strip it out for bundle-restricted tenants.
const EXPERIMENT_ONLY_OPTION: FilterOption = { value: "experiment_only", name: "Coding Exercises" };

// svc-content ids come back PascalCase (e.g. AppLab); Rails' lesson_type param expects snake_case.
const toRailsLessonType = (id: string): string =>
  CONTENT_TYPE_TO_LESSON_TYPE[id] ?? id.split(/(?=[A-Z])/).join("_").toLowerCase();

export const setActivityType = (contentTypeOptions: CatalogTagOption[] = []): FilterOption[] => [
  ...contentTypeOptions.map(o => ({
    value: toRailsLessonType(o.id),
    name: o.displayName,
  })),
  EXPERIMENT_ONLY_OPTION,
];

const RELEASE_OPTIONS: FilterOption[] = [{ value: "recently", name: "Recently Released" }];

export const setRelease = (): FilterOption[] => RELEASE_OPTIONS;

export const buildFilterOptions = (filterData: {
  tagsData: Record<string, CatalogTagOption[]>;
  experimentLanguagesList: Array<{ name: string }>;
  experimentDifficultiesList: Array<{ name: string }>;
}): FilterGroup[] => [
  {
    title: "Language",
    label: "language",
    filters: setLanguages({
      experimentLanguagesList: filterData.experimentLanguagesList,
      tagsData: filterData.tagsData,
    }),
  },
  {
    title: "Technology",
    label: "technology",
    filters: setTechnologies({ tagsData: filterData.tagsData }),
  },
  {
    title: "Role",
    label: "role",
    filters: setFocus({ tagsData: filterData.tagsData }),
  },
  {
    title: "Level",
    label: "level",
    filters: setLevel({ experimentDifficultiesList: filterData.experimentDifficultiesList }),
  },
  {
    title: "Activity Type",
    label: "activityType",
    filters: setActivityType(filterData.tagsData.contentType),
  },
  {
    title: "Release",
    label: "release",
    filters: setRelease(),
  },
];
